<template>
  <div 
    v-if="visible && banners.length > 0" 
    class="announcement-marquee-bar bg-slate-900 dark:bg-slate-950 text-white relative z-30 overflow-hidden shadow-md border-b border-slate-800 transition-all duration-300"
  >
    <div class="max-w-7xl mx-auto px-4 py-2 sm:py-2.5 flex items-center justify-between gap-3 text-xs sm:text-sm font-bold relative">
      
      <!-- Sliding Marquee Ticker Container -->
      <div 
        class="flex-1 overflow-hidden relative cursor-pointer select-none group"
        @mouseenter="isPaused = true"
        @mouseleave="isPaused = false"
        @touchstart="isPaused = true"
        @touchend="isPaused = false"
      >
        <div 
          class="marquee-track flex items-center gap-8 whitespace-nowrap"
          :style="{ animationPlayState: isPaused ? 'paused' : 'running' }"
        >
          <!-- Duplicate list for seamless infinite looping -->
          <div 
            v-for="(banner, index) in displayList" 
            :key="`b-${index}`" 
            class="inline-flex items-center gap-2.5 shrink-0"
          >
            <!-- Badge Tag with Per-Theme Colors -->
            <span 
              v-if="banner.badge_text" 
              :class="[
                'px-2.5 py-0.5 rounded-full text-[10px] uppercase font-black tracking-wider border shrink-0 shadow-sm',
                getBadgeThemeClass(banner.theme)
              ]"
            >
              {{ banner.badge_text }}
            </span>

            <!-- Message Text -->
            <span class="tracking-tight font-extrabold text-slate-100 drop-shadow-sm">
              {{ banner.message }}
            </span>

            <!-- Optional Link Button -->
            <router-link 
              v-if="banner.link_url && banner.link_url.startsWith('/')" 
              :to="banner.link_url"
              :class="[
                'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-black uppercase tracking-wider border transition-all shadow-sm ml-1',
                getButtonThemeClass(banner.theme)
              ]"
            >
              <span>{{ banner.link_text || 'View' }}</span>
              <span>&rarr;</span>
            </router-link>
            <a 
              v-else-if="banner.link_url"
              :href="banner.link_url"
              target="_blank"
              :class="[
                'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-black uppercase tracking-wider border transition-all shadow-sm ml-1',
                getButtonThemeClass(banner.theme)
              ]"
            >
              <span>{{ banner.link_text || 'View' }}</span>
              <span>&rarr;</span>
            </a>

            <!-- Separator Dot -->
            <span class="text-slate-600 font-black ml-4">•</span>
          </div>
        </div>
      </div>

      <!-- Dismiss Button -->
      <button 
        @click="dismissBanner" 
        class="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0 z-10"
        title="Dismiss Announcement Bar"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api, { announcementService } from '@/services/api.js';

const banners = ref([]);
const visible = ref(true);
const isPaused = ref(false);

const displayList = computed(() => {
  if (banners.value.length === 0) return [];
  // Repeat list 4 times so marquee flows seamlessly across wide screens
  return [...banners.value, ...banners.value, ...banners.value, ...banners.value];
});

const getBadgeThemeClass = (theme) => {
  switch (theme) {
    case 'success':
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40';
    case 'warning':
      return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    case 'danger':
      return 'bg-rose-500/20 text-rose-300 border-rose-500/40';
    case 'info':
      return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40';
    case 'primary':
    default:
      return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
  }
};

const getButtonThemeClass = (theme) => {
  switch (theme) {
    case 'success':
      return 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400/40';
    case 'warning':
      return 'bg-amber-600 hover:bg-amber-500 text-white border-amber-400/40';
    case 'danger':
      return 'bg-rose-600 hover:bg-rose-500 text-white border-rose-400/40';
    case 'info':
      return 'bg-cyan-600 hover:bg-cyan-500 text-white border-cyan-400/40';
    case 'primary':
    default:
      return 'bg-blue-600 hover:bg-blue-500 text-white border-blue-400/40';
  }
};

const fetchBanners = async () => {
  try {
    const res = await announcementService.getActiveAnnouncements();
    let list = [];
    if (res && res.success && Array.isArray(res.data)) {
      list = res.data;
    } else if (res && Array.isArray(res.data)) {
      list = res.data;
    } else if (Array.isArray(res)) {
      list = res;
    }
    banners.value = list;
  } catch (error) {
    console.error('Failed to fetch announcements:', error);
  }
};

const dismissBanner = () => {
  visible.value = false;
  sessionStorage.setItem('eskrag_announcement_dismissed', 'true');
};

onMounted(() => {
  const dismissed = sessionStorage.getItem('eskrag_announcement_dismissed');
  if (dismissed === 'true') {
    visible.value = false;
  }
  fetchBanners();
});
</script>

<style scoped>
.marquee-track {
  display: flex;
  width: max-content;
  animation: marquee-slide 28s linear infinite;
}

@keyframes marquee-slide {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
